"use client"
import SearchBar from "@/components/ui/Search/SearchBar"
import { generateNavLinks } from "@/lib/navigation-links"
import { User } from "@/app/generated/prisma"
import { Routes } from "@/lib/routes"
import { ContentNavItem } from "@/types"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { buttonVariants } from "../../Button"
import { SecondaryNavBar } from "./secondaryNavbar"
import { UserTile } from "./userTile"
import NavItemDesktop from "@/utils/navbar/desktop/NavItemDesktop"
import { useSisterSiteNavigation } from "@/utils/navbar/useSisterSiteNavigation"
import { useActiveNav } from "@/lib/active-nav-context"
import { GoPerson } from "react-icons/go"
import { SisterSiteKey } from "@/types/auth"
import { loginPathFor, RAAUTH_ONLY, signupPathFor } from "@/lib/auth-stack"

export function DesktopNavBar({
   isAuthenticated,
   isSessionLoading,
   user,
   latestVideoSlug,
   playlists,
   isSecondaryHidden,
}: {
   isAuthenticated: boolean
   isSessionLoading: boolean
   // Passed down rather than re-read from useSession(): the Header already resolved the
   // auth state, and a second independent read is how the navbars came to disagree.
   user: Partial<User> | undefined
   latestVideoSlug: string
   playlists: string[]
   isSecondaryHidden: boolean
}) {
   const navigationLinks = generateNavLinks(latestVideoSlug)
   const { buildHandler, buildPrefetch } = useSisterSiteNavigation()

   const [secondaryStaticNavItems, setSecondaryStaticNavItems] = useState<Array<ContentNavItem>>([])

   const { activeNavHref } = useActiveNav()
   const currentPath = usePathname()
   async function staticSecondaryNavItems() {
      if (currentPath.startsWith("/resources")) {
         const resourcesLink = navigationLinks.find((link) => link.title === "Resources")
         if (resourcesLink) {
            setSecondaryStaticNavItems(resourcesLink.content || [])
         }
      } else if (currentPath.startsWith("/blog")) {
         const topicsLink = navigationLinks.find((link) => link.title === "Blog")
         if (topicsLink) {
            setSecondaryStaticNavItems(topicsLink.content.sort((a, b) => a.title.localeCompare(b.title)) || [])
         }
      } else if (currentPath.startsWith("/shows")) {
         if (playlists) {
            setSecondaryStaticNavItems(
               playlists.map((title) => ({
                  title: title,
                  href: Routes.client.showChannel.getPath({ slug: title }),
                  icon: () => "",
               })) || [],
            )
         }
      } else if (activeNavHref === "/blog/") {
         const topicsLink = navigationLinks.find((link) => link.title === "Blog")
         if (topicsLink) {
            setSecondaryStaticNavItems(topicsLink.content.sort((a, b) => a.title.localeCompare(b.title)) || [])
         }
      } else {
         const newsLink = navigationLinks.find((link) => link.title === "News")
         if (newsLink) {
            setSecondaryStaticNavItems(newsLink.content || [])
         }
      }
   }
   useEffect(() => {
      staticSecondaryNavItems()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentPath, activeNavHref])

   return (
      <>
         <div
            className=" relative z-20 flex w-full flex-col"
            style={{
               position: "relative",
               zIndex: 100,
            }}
         >
            <div
               className="w-full bg-nav-bg"
               style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  zIndex: 100,
               }}
            >
               <div
                  className="w-full bg-nav-bg"
                  style={{
                     boxShadow: "0 4px 8px 0px #1A1A1E1F",
                     position: "relative",
                  }}
               >
                  <div className="flex w-full px-4 py-3">
                     <div className=" flex flex-1  items-center justify-between gap-2 text-[#0a0a0a] xl:gap-4">
                        <div className="flex h-[60px] w-[200px] shrink-0 items-center justify-start">
                           <Link
                              aria-label="Navigate to home page"
                              href={"/"}
                              className="relative block h-[60px] w-[60px]"
                           >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                 src="/_logos/logo-restaurant-association-icon-only.svg"
                                 alt="Restaurant Association"
                                 width={60}
                                 height={60}
                                 fetchPriority="high"
                                 className="absolute inset-0 h-full w-full transform-gpu [.dark_&]:hidden"
                              />
                           </Link>
                        </div>

                        <nav role="navigation" aria-label="Main navigation">
                           <ol className="flex ">
                              {navigationLinks.map((link) => {
                                 let baseHref = link.href ?? ""

                                 const sisterSite: { [key: string]: SisterSiteKey } = {
                                    Academy: "training",
                                    Jobs: "jobs",
                                    Events: "events",
                                 }

                                 const isSisterSite: boolean = link.title in sisterSite && !RAAUTH_ONLY
                                 const sisterSiteKey: SisterSiteKey = sisterSite[link.title]
                                 const onNavigate = isSisterSite ? buildHandler(baseHref, sisterSiteKey) : undefined
                                 const onPrefetch = isSisterSite ? buildPrefetch(sisterSiteKey) : undefined

                                 return (
                                    <NavItemDesktop
                                       key={`${link.title}_desktop_nav`}
                                       href={baseHref}
                                       label={link.title}
                                       onNavigate={onNavigate}
                                       onPrefetch={onPrefetch}
                                    />
                                 )
                              })}
                           </ol>
                        </nav>
                        {/* </div> */}

                        <div className="relative flex min-w-[180px] shrink-0 items-center justify-end gap-2 ">
                           <SearchBar
                              toggleNavMenu={() => {}}
                              showText={false}
                              className="relative z-[999] w-[46px] 2xl:hidden"
                           />
                           <SearchBar
                              toggleNavMenu={() => {}}
                              className="relative z-[999] hidden w-[262px] 2xl:inline-flex"
                           />
                           {isSessionLoading ? (
                              <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200" />
                           ) : isAuthenticated && user ? (
                              <UserTile user={user} />
                           ) : (
                              <Link
                                 className="flex h-10 w-10 shrink-0 items-center justify-center overflow-visible rounded-lg bg-custom-primary "
                                 aria-label="Login"
                                 href={loginPathFor(currentPath)}
                              >
                                 <GoPerson className="h-5 w-5 text-[#FAECEC]" aria-hidden="true" />
                              </Link>
                           )}

                           {!isSessionLoading && !isAuthenticated && (
                              <div className="flex shrink-0 flex-nowrap items-center justify-center gap-2">
                                 <Link
                                    href={signupPathFor(currentPath)}
                                    className={buttonVariants({
                                       variant: "outline",
                                       size: "lg",
                                       className:
                                          "border-brand-orange !text-base text-brand-orange hover:bg-brand-orange/10",
                                    })}
                                 >
                                    Join Free
                                 </Link>
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               </div>

               {!isSecondaryHidden && <SecondaryNavBar isOpen={true} items={secondaryStaticNavItems} />}
            </div>
         </div>
      </>
   )
}
